import { type PreparedShape, type Rng, type ShapeDefinition, type ShapeSink } from '../shape.js';
import { hash01, onDisc, onSphere, revolve, TAU } from './helpers.js';

/* ── Galaxy ────────────────────────────────────────────────────────────── */

export const galaxy: ShapeDefinition = {
  name: 'galaxy',
  palette: { base: 0x40_28_7e, shade: 0x6a_94_f0, accent: 0xff_e4_b0 },
  driftRate: 0.25,
  framing: 2.29,
  prepare: ({ random, total, variant }): PreparedShape => {
    const arms = 2 + Math.floor(variant * 3);
    const twist = 2.6 + variant * 1.8;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.78) {
        // Logarithmic arms: angle grows with the log of the radius.
        const t = Math.sqrt(random());
        const r = 1.2 + t * 14;
        const arm = Math.floor(random() * arms);
        const scatter = (random() - 0.5) * (0.55 / (0.25 + t));
        const angle = Math.log(r) * twist + (arm / arms) * TAU + scatter;
        sink.x = Math.cos(angle) * r;
        sink.z = Math.sin(angle) * r;
        sink.y = (random() - 0.5) * (1.6 / (0.4 + t * 3));
        // A dust lane trailing each arm keeps the spiral legible.
        const dust = Math.abs(scatter) < 0.12 ? 0.5 : 0;
        sink.tint = Math.min(1, 0.2 + random() * 0.4 + dust * 0.35 - t * 0.15);
      } else if (p < 0.92) {
        // Core bulge.
        onSphere(sink, random, Math.pow(random(), 0.6) * 2.8);
        sink.y *= 0.7;
        sink.tint = 0.85 + random() * 0.15;
      } else {
        // Halo and globular clusters.
        onSphere(sink, random, 8 + random() * 12);
        sink.tint = 0.55 + random() * 0.3;
        sink.driftY = 0.2;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Nautilus ──────────────────────────────────────────────────────────── */

const SHELL_START = 0.24;
const SHELL_OUTER = 6.4;

export const nautilus: ShapeDefinition = {
  name: 'nautilus',
  palette: { base: 0x8a_44_26, shade: 0xf4_e0_b8, accent: 0xff_f8_e8 },
  driftRate: 0.4,
  framing: 2.56,
  prepare: ({ random, total, variant }): PreparedShape => {
    const turns = 3 + variant * 0.9;
    // Solve the growth rate so the outermost whorl always lands on the same
    // radius: a fixed rate makes the shell's size explode with the turn count.
    const growth = Math.log(SHELL_OUTER / SHELL_START) / (turns * TAU);
    const chambers = 26 + Math.floor(variant * 12);
    const sweep = turns * TAU;
    // Arc length grows with the radius, so a uniform `t` piles most particles
    // into the tiny inner whorls and burns out the core. Sampling `t` from the
    // exponential's own distribution spreads them evenly along the shell.
    const outer = Math.exp(growth * sweep);
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const t = Math.log(1 + random() * (outer - 1)) / growth;
      const r = SHELL_START * Math.exp(growth * t);
      const tube = r * 0.42;
      if (p < 0.72) {
        // A tube swept along the spiral, thickening as it goes.
        const around = random() * TAU;
        const bulge = Math.cos(around) * tube;
        sink.x = Math.cos(t) * (r + bulge);
        sink.y = Math.sin(t) * (r + bulge);
        sink.z = Math.sin(around) * tube;
        // Tiger stripes running across the shell.
        const stripe = Math.abs(Math.sin(t * 4.6)) > 0.62 ? 0.75 : 0.2;
        sink.tint = stripe + random() * 0.12;
      } else if (p < 0.88) {
        // Septa: the internal walls between chambers. Biased outward for the
        // same reason as the shell itself — the inner chambers are tiny.
        const chamber = Math.floor(random() * chambers) / chambers;
        const at = Math.log(1 + chamber * (outer - 1)) / growth;
        const cr = SHELL_START * Math.exp(growth * at);
        const across = random();
        const around = random() * TAU;
        sink.x = Math.cos(at) * cr * (1 - across * 0.9) + Math.cos(around) * cr * 0.3 * across;
        sink.y = Math.sin(at) * cr * (1 - across * 0.9) + Math.sin(around) * cr * 0.3 * across;
        sink.z = (random() - 0.5) * cr * 0.7;
        sink.tint = 0.66 + random() * 0.2;
      } else {
        // Water moving past the aperture.
        const at = sweep;
        const ar = SHELL_START * Math.exp(growth * at);
        onSphere(sink, random, ar * 0.9);
        sink.x += Math.cos(at) * ar;
        sink.y += Math.sin(at) * ar;
        sink.tint = 0.95;
        sink.driftX = 0.6;
        sink.driftY = 0.4;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Lanterns ──────────────────────────────────────────────────────────── */

const LANTERN_COUNT = 70;

/** One paper lantern: a glowing barrel of rice paper with dark caps. */
function lanternBody(sink: ShapeSink, random: Rng, lamp: number, ceiling: number): void {
  const rise = hash01(lamp * 3.7);
  revolve(sink, random, (v) => 0.95 * Math.sin(v * Math.PI) ** 0.3, 1.8);
  const isCap = sink.y < 0.16 || sink.y > 1.64;
  sink.x += (hash01(lamp) - 0.5) * 30;
  sink.z += (hash01(lamp * 7.1) - 0.5) * 30;
  sink.y += 1.6 + rise * ceiling;
  sink.tint = isCap ? 0.24 : 0.8 + random() * 0.2;
  sink.driftX = 0.35 + rise * 0.5;
  sink.driftY = 0.4;
  sink.phase = lamp * 1.7;
}

function placeLanterns(sink: ShapeSink, random: Rng, ceiling: number, p: number): void {
  const lamp = Math.floor(random() * LANTERN_COUNT);
  if (p < 0.56) {
    lanternBody(sink, random, lamp, ceiling);
  } else if (p < 0.74) {
    // Every lantern doubled in the water, dimmer and stretched.
    lanternBody(sink, random, lamp, ceiling);
    sink.y = -sink.y * 0.55 - 0.1;
    sink.tint = Math.min(1, sink.tint * 0.55);
    sink.driftX = 0.5;
    sink.driftZ = 0.5;
  } else if (p < 0.9) {
    // The water itself, black with a slow swell across it.
    onDisc(sink, random, 21);
    sink.y = -0.02 + Math.sin(sink.x * 0.8 + sink.z * 0.45) * 0.06;
    const swell = Math.abs(Math.sin(sink.x * 0.8 + sink.z * 0.45));
    sink.tint = 0.05 + swell * 0.3;
    sink.driftY = 0.12;
    sink.phase = sink.x * 0.8 + sink.z * 0.45;
  } else {
    // Warm haze between the lanterns.
    onDisc(sink, random, 18);
    sink.y = 1 + random() * ceiling;
    sink.tint = 0.5 + random() * 0.25;
    sink.driftY = 0.5;
    sink.driftX = 0.4;
    sink.phase = hash01(Math.floor(sink.y * 313)) * TAU;
  }
}

export const lanterns: ShapeDefinition = {
  name: 'lanterns',
  palette: { base: 0x2e_1a_40, shade: 0xff_9a_4c, accent: 0xff_f0_c0 },
  driftRate: 0.5,
  framing: 1.89,
  prepare: ({ random, total, variant }): PreparedShape => {
    const ceiling = 16 + variant * 9;
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeLanterns(sink, random, ceiling, index / total);
      },
    };
  },
};

/* ── Wheat ─────────────────────────────────────────────────────────────── */

export const wheat: ShapeDefinition = {
  name: 'wheat',
  palette: { base: 0x6a_50_20, shade: 0xe4_bc_50, accent: 0xff_f0_a8 },
  driftRate: 0.9,
  framing: 1.76,
  prepare: ({ random, total, variant }): PreparedShape => {
    const height = 2.2 + variant * 1.2;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const x = (random() - 0.5) * 40;
      const z = (random() - 0.5) * 40;
      sink.x = x;
      sink.z = z;
      // Wind phase comes from position, so gusts sweep across the field as
      // coherent waves instead of every stalk twitching on its own.
      sink.phase = x * 0.22 + z * 0.13;
      if (p < 0.58) {
        const up = random();
        sink.y = up * height;
        sink.tint = 0.1 + up * 0.3;
        sink.driftX = up * up * 0.55;
        sink.driftZ = up * up * 0.2;
      } else if (p < 0.88) {
        // Seed heads: the bright band that gives the field its surface.
        const up = 0.85 + random() * 0.25;
        sink.y = up * height + Math.sin(x * 0.5 + z * 0.3) * 0.1;
        sink.z += Math.sin(up * 8) * 0.05;
        sink.tint = 0.55 + random() * 0.35;
        sink.driftX = 0.7;
        sink.driftZ = 0.25;
      } else if (p < 0.96) {
        // Bare earth showing between the stalks.
        sink.y = 0.02;
        sink.tint = 0.02 + random() * 0.06;
      } else {
        // Chaff and seed drifting in the light.
        sink.y = height * (0.6 + random() * 1.6);
        sink.tint = 0.92 + random() * 0.08;
        sink.driftX = 1;
        sink.driftY = 0.45;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};
